import { Component, ElementRef, OnInit, Optional, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { OnboardingDialogResult } from '@app/+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { RouteDialogDirective } from '@app/+modals/route-dialog.directive';
import { handleSubmitError } from '@app/forms/form.helper';
import { EmployeeModel } from '@app/reducers/orm/employee/employee.model';
import { SharedLaddaModule } from '@app/shared/shared-ladda.module';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getAuthenticatedUserId } from '@reducers/auth/auth.service';
import { AppState } from '@reducers/index';
import { EmployeeService } from '@reducers/orm/employee/employee.service';
import { ButtonComponent, DialogRef, SbDialogModule, ToastService } from '@sb/ui';
import { combineLatestWith, finalize, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';

import { UserGuideType } from '../../../reducers/orm/user-guide';
import { SmbHospitalityOnboardingChecklistStep } from '../../../shared/onboarding/onboarding-checklist.model';
import { OnboardingChecklistService } from '../../../shared/onboarding/onboarding-checklist.service';
import { OnboardingUtils } from '../../onboarding.utils';
import { EmployeesTableComponent } from '../employees-table/employees-table.component';

export interface InviteEmployeesDialogResult extends OnboardingDialogResult {
  count: number;
}

@Component({
  selector: 'invite-employees-dialog',
  standalone: true,
  imports: [
    TranslateModule,
    SharedLaddaModule,
    ButtonComponent,
    EmployeesTableComponent,
    SbDialogModule,
    RouteDialogDirective,
  ],
  templateUrl: './invite-employees.component.html',
})
export class InviteEmployeesDialogComponent implements OnInit {
  @ViewChild(RouteDialogDirective, { static: true })
  public modal: RouteDialogDirective;

  @ViewChildren('firstNameInput', { read: ElementRef })
  public firstNameInputs: QueryList<ElementRef<HTMLInputElement>>;

  public form = new FormGroup({
    rows: new FormArray([]),
  });

  public get rows(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  public isLoading = false;

  public authenticatedUserId: string;

  public get selectedRowsCount(): number {
    return this.form.value.rows?.reduce((acc, row) => acc + (row.selected ? 1 : 0), 0) || 0;
  }

  public constructor(
    @Optional()
    public readonly dialogRef: DialogRef<InviteEmployeesDialogResult>,
    private readonly store: Store<AppState>,
    private readonly employeeService: EmployeeService,
    private readonly onboardingUtils: OnboardingUtils,
    private readonly onboardingChecklist: OnboardingChecklistService,
    private readonly translate: TranslateService,
    private readonly toast: ToastService,
  ) {
    if (this.dialogRef) {
      this.dialogRef.disableClose = true;
    }
  }

  public ngOnInit(): void {
    void this.store
      .select(getAuthenticatedUserId)
      .pipe(take(1))
      .subscribe((authenticatedUserId) => {
        this.authenticatedUserId = authenticatedUserId;
      });
  }

  public submit(): void {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    this.isLoading = true;

    const employees = [];
    const requests: Observable<unknown>[] = [];

    this.form.value.rows.forEach((employee) => {
      const { batchPayload, updateTeamsPayload } =
        this.onboardingUtils.mapEmployeeForBatchRequestAndUpdateTeamRequests(employee);

      employees.push(batchPayload);

      if (updateTeamsPayload) {
        requests.push(this.employeeService.saveTeams(updateTeamsPayload.employeeId, updateTeamsPayload.payload));
      }
    });

    // Since there might be rows with and without id
    // and the order in the response isn't the same as the request payload
    // we identify employees by their emails (which are unique)
    if (this.form.pristine) {
      requests.push(of(this.getIdsByEmail(employees)));
    } else {
      requests.push(
        this.employeeService.saveMany({ User: employees }, { headers: { ignoreMetaMessage: true } }).pipe(
          // Map of emails and IDs
          map((response) => this.getIdsByEmail(Object.values(response.entities.employees || {}))),
        ),
      );
    }
    void forkJoin(requests)
      .pipe(
        map((responses) => {
          const idsByEmail = responses.pop() as Record<string, string>;
          return this.form.value.rows.filter((row) => row.selected).map((row) => idsByEmail[row.email]) as string[];
        }),
        switchMap((selectedIds) =>
          this.employeeService
            .resendInvite({ ids: selectedIds })
            // pass selected count downstream be used when closing the dialog
            .pipe(map(() => selectedIds.length)),
        ),
        combineLatestWith(
          this.onboardingChecklist.completeStep(
            UserGuideType.SELF_ONBOARDING_CHECKLIST,
            SmbHospitalityOnboardingChecklistStep.INVITE_TEAM,
          ),
        ),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: ([selectedCount]) => {
          this.dialogRef?.close({ action: 'primary', count: selectedCount });
          if (!this.dialogRef) {
            this.modal.close();
          }
        },
        error: (res) => {
          if (res?.error?.meta.validation_errors) {
            handleSubmitError(res, this.rows);
            this.toast.openDanger(res.error.meta.status_message);
          } else {
            this.toast.openDanger(this.translate.instant('Something went wrong'));
          }
        },
      });
  }

  public skip() {
    const count = this.form.value.rows.filter((row) => row.selected).length;
    this.dialogRef?.close({ action: 'secondary', count });
    if (!this.dialogRef) {
      this.modal.close();
    }
  }

  private getIdsByEmail(employees: EmployeeModel[]) {
    return employees.reduce(
      (idsByEmail, employee) => {
        if (employee.email) {
          idsByEmail[employee.email] = employee.id;
        }

        return idsByEmail;
      },
      {} as Record<string, string>,
    );
  }
}
